import * as React from "react";
import { PropertyHelper } from "../../../../shared/utility/property-helper";
import { KDatatimePicker } from "../../../data-entry/k-datetime-picker";
import KuikaDatePicker, { KuikaDatePickerMode } from "../../../ui-kit/kuika-date-picker/kuika-date-picker";

export interface IPropertyFormDateTimePropertyState {
  /** Absent when the property has no value — the picker shows its placeholder. */
  value: string | undefined;
}

export interface IPropertyFormDateTimePropertyProps {
  value: string;
  /** `undefined` when the field is cleared — the same absent value the antd picker emitted. */
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  isDateTime?: boolean;
  isTime?: boolean;
  /** Overrides the default "Select date", too long for narrow hosts. */
  placeholder?: string;
}

/**
 * The date / date-time editor behind a component property, an action parameter and a condition's
 * fixed value.
 *
 * Date and date-time are the kit's `KuikaDatePicker`. It writes the same two shapes the antd
 * picker wrote before it — `YYYY-MM-DD` and `YYYY-MM-DDTHH:mm:ss` — which matters more here than
 * anywhere else the kit is used: this string is stored verbatim in the screen and read back by the
 * generated code, so a picker that reformatted what it was handed would rewrite saved conditions
 * on sight. It does not: the value is only ever written from an explicit pick, never normalised
 * on mount, and the calendar renders DD/MM/YYYY without the browser locale getting a vote.
 *
 * Time-only (`minTime` / `maxTime` / `disableBeforeTime` / `disableAfterTime`) stays on the antd
 * `TimePicker` — the kit has no time-only control, and inventing one here would be a worse answer
 * than the one exception.
 */
export class PropertyFormDateTimeProperty extends React.PureComponent<
  IPropertyFormDateTimePropertyProps,
  IPropertyFormDateTimePropertyState
> {
  propertyHelper: PropertyHelper;

  constructor(props: IPropertyFormDateTimePropertyProps) {
    super(props);
    this.propertyHelper = new PropertyHelper();
    this.state = {
      value: this.props.value
    };
  }

  componentDidUpdate = (prevProps: IPropertyFormDateTimePropertyProps) => {
    if (prevProps === this.props) return;
    this.setState({ value: this.getPropertyValueFromProps() });
  };

  getPropertyValueFromProps = (): string | undefined => {
    return this.props.value ? this.props.value : undefined;
  };

  handleChangeValue = (value: string | undefined) => {
    this.setState({ value }, () => {
      if (this.props.onChange) {
        this.props.onChange(value);
      }
    });
  };

  /**
   * Clearing used to reach the consumer as `undefined` (antd handed the formatter a null date and
   * got nothing back); the kit's picker says `null`. Kept as `undefined` so a cleared property is
   * the same absent value it has always been.
   */
  handleKitChange = (next: string | null): void => {
    this.handleChangeValue(next ?? undefined);
  };

  getPlaceholder = (): string => {
    if (this.props.placeholder !== undefined && this.props.placeholder.length > 0) {
      return this.props.placeholder;
    }
    return this.props.isDateTime === true ? "Select date & time" : "Select date";
  };

  render() {
    const mode: KuikaDatePickerMode = this.props.isDateTime === true ? "datetime" : "date";
    return (
      <div className="kuika_property-grid-column kuika_select_property kuika-right-panel__col">
        {this.props.isTime === true ? (
          <KDatatimePicker
            allowClear
            value={this.state.value}
            onChange={this.handleChangeValue}
            placeholder={this.props.placeholder}
            className="kuika_property-grid-input"
            display="Time"
            disabled={this.props.disabled}
          />
        ) : (
          <KuikaDatePicker
            mode={mode}
            // The same full-width, md control as the text and number inputs these rows sit beside.
            size="md"
            block
            showClear
            className="kuika_property-grid-input"
            value={this.state.value ?? ""}
            placeholder={this.getPlaceholder()}
            disabled={this.props.disabled}
            onChange={this.handleKitChange}
          />
        )}
      </div>
    );
  }
}
